import { useEffect, useRef, useState } from 'react';
import { api } from '../utils/api.js';
import { docFiles } from '../utils/docFileCache.js';

/**
 * Inline thumbnail of a single document page.
 *
 * Cheapest source first (DayBook card "Thumbnails use the stored page image"):
 *   1. Nothing loads until the thumbnail is near the screen (IntersectionObserver).
 *   2. The page image the worker already stores (migration 0011) — one small JPEG.
 *   3. Only if there is none: the document itself, through docFileCache — one
 *      download and one pdf.js document per doc, shared by every thumbnail and
 *      freed shortly after the last one unmounts. A scanned sheet (an image
 *      document) is shown directly.
 * Before, every thumbnail downloaded and parsed the whole PDF and never freed
 * it, which on a long session meant dozens of 10 MB downloads in one tab.
 *
 * Props:
 *   docId   — integer database id of the document.
 *   page    — 1-based page number for PDFs (default 1).
 *   onClick — optional handler for tap-to-expand. Caller decides whether
 *             to open a lightbox, deep-link to the file, etc.
 *   label   — optional override for the caption (defaults to "Doc #N · pg M/T").
 *
 * Failure modes:
 *   - 404 / network error → shows a small "Doc unavailable" tile so the
 *     chat keeps reading naturally instead of breaking the layout.
 *   - PDF page out of range → clamps to last page.
 */
export default function DocPageThumb({ docId, page = 1, onClick, label }) {
  const wrapperRef = useRef(null);
  const canvasRef = useRef(null);
  // 'waiting' (off screen) | 'page-image' | 'loading' | 'pdf-ready' | 'image' | 'error'
  const [status, setStatus] = useState('waiting');
  const [meta, setMeta] = useState({ pageNum: page, totalPages: null });
  const [imgUrl, setImgUrl] = useState(null);
  const [visible, setVisible] = useState(false);

  // 1. Start only once the thumbnail is near the screen.
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return undefined;
    }
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        setVisible(true);
        io.disconnect();
      }
    }, { rootMargin: '300px' });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // 2. Try the stored page image.
  useEffect(() => {
    if (!visible) return;
    setMeta({ pageNum: page, totalPages: null });
    setImgUrl(api.documentPageImageUrl(docId, page));
    setStatus('page-image');
  }, [visible, docId, page]);

  // 3. No stored image (the <img> errored): fall back to the shared document.
  const [needsFallback, setNeedsFallback] = useState(false);
  useEffect(() => { setNeedsFallback(false); }, [docId, page]);
  useEffect(() => {
    if (!needsFallback) return undefined;
    let cancelled = false;
    setStatus('loading');
    // Exactly one release per acquire, even if this unmounts mid-download.
    const acquired = docFiles.acquire(docId);
    (async () => {
      try {
        const doc = await acquired;
        if (cancelled) return;
        if (doc.kind === 'image') {
          setImgUrl(doc.url);
          setStatus('image');
          return;
        }
        const pageNum = Math.min(Math.max(1, page), doc.pdf.numPages);
        const pdfPage = await doc.pdf.getPage(pageNum);
        const canvas = canvasRef.current;
        if (cancelled || !canvas) return;
        // 1.5x — sharper than 1x on a Retina iPad without a huge canvas.
        const viewport = pdfPage.getViewport({ scale: 1.5 });
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        await pdfPage.render({ canvasContext: canvas.getContext('2d'), viewport }).promise;
        if (cancelled) return;
        setMeta({ pageNum, totalPages: doc.pdf.numPages });
        setStatus('pdf-ready');
      } catch (err) {
        if (!cancelled) {
          console.warn(`DocPageThumb #${docId}p${page} failed:`, err);
          setStatus('error');
        }
      }
    })();
    return () => {
      cancelled = true;
      acquired.then(() => docFiles.release(docId), () => {});
    };
  }, [needsFallback, docId, page]);

  // Build caption
  const caption = label || (
    status === 'pdf-ready' && meta.totalPages
      ? `Doc #${docId} · pg ${meta.pageNum}/${meta.totalPages}`
      : status === 'page-image'
        ? `Doc #${docId} · pg ${page}`
        : `Doc #${docId}`
  );

  const Wrapper = onClick ? 'button' : 'div';

  return (
    <Wrapper
      ref={wrapperRef}
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      className={[
        'block w-40 sm:w-48 bg-ink-900 border border-ink-700 rounded-lg overflow-hidden relative',
        'aspect-[3/4]',
        onClick
          ? 'hover:border-mra-red active:scale-[0.98] transition cursor-pointer'
          : '',
      ].join(' ')}
      aria-label={`Document ${docId}${meta.pageNum ? ` page ${meta.pageNum}` : ''}`}
    >
      {/* Image variant: the stored page image, or a scanned sheet */}
      {(status === 'page-image' || status === 'image') && imgUrl && (
        <img
          src={imgUrl}
          alt=""
          className="absolute inset-0 w-full h-full object-contain bg-ink-950"
          onError={status === 'page-image' ? () => setNeedsFallback(true) : () => setStatus('error')}
        />
      )}

      {/* PDF canvas — always in DOM so the ref is stable for the effect.
          We hide it until the page actually renders. */}
      <canvas
        ref={canvasRef}
        className={[
          'absolute inset-0 w-full h-full bg-ink-950',
          status === 'pdf-ready' ? 'block object-contain' : 'opacity-0 pointer-events-none',
        ].join(' ')}
        // Tailwind doesn't have an exact "object-contain" for canvases — the
        // canvas natural size + width/height: 100% gives the same effect.
        style={{ objectFit: 'contain' }}
      />

      {/* Loading skeleton */}
      {(status === 'loading' || status === 'waiting') && (
        <div className="absolute inset-0 flex items-center justify-center bg-ink-800/90 animate-pulse-soft">
          <span className="text-ink-300 text-xs font-mono uppercase tracking-wider">
            Loading…
          </span>
        </div>
      )}

      {/* Error state — quiet, doesn't break the chat layout */}
      {status === 'error' && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-ink-800 border-2 border-fail/40 p-2 text-center">
          <span className="text-fail-400 text-2xl leading-none">⚠</span>
          <span className="text-ink-300 text-xs mt-1">Doc unavailable</span>
        </div>
      )}

      {/* Caption strip */}
      <div className="absolute bottom-0 inset-x-0 px-2 py-1 text-[10px] font-mono uppercase tracking-wider text-ink-400 bg-ink-900/95 border-t border-ink-700">
        {caption}
      </div>
    </Wrapper>
  );
}
